import React from "react";
import Marquee from "react-fast-marquee";

/**
 * FestivalHighlights Component
 *
 * Reusable component designed according to ISKCON festival highlights specifications.
 * Supports smooth auto-scrolling marquee with pause-on-hover.
 * Receives images, titles, and descriptions dynamically from the parent component.
 *
 * @param {Array} items - List of highlight objects: [{ id, image, title, description, badge, alt }]
 * @param {string} title - Section title (default: "Festival Highlights")
 * @param {string} subtitle - Optional section subtitle/tagline
 * @param {string} className - Optional additional container class
 * @param {string} cardWidth - Width of individual cards (default: "w-[260px] sm:w-[280px] md:w-[300px]")
 * @param {number} speed - Auto-scroll speed (default: 45)
 * @param {boolean} pauseOnHover - Pause scrolling on mouse hover (default: true)
 * @param {string} layout - "scroll" (auto-scrolling marquee) or "grid" (responsive grid)
 */
const FestivalHighlights = ({
  items = [],
  title = "Festival Highlights",
  subtitle = "",

  cardWidth = "w-[260px] sm:w-[280px] md:w-[300px]",
  speed = 45,
  pauseOnHover = true,
  layout = "scroll",
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`w-full py-6 sm:py-10 overflow-hidden `}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-2 sm:mb-4 text-center">
        {/* Section Heading */}
        {title && (
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-red tracking-tight inline-block">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-slate-600 text-sm sm:text-base mt-2 max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
      </div>

      {/* Layout: Auto-Scrolling Marquee Slider or Grid */}
      {layout === "grid" ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {items.map((item, index) => (
              <HighlightCard key={item.id || index} item={item} />
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full relative py-2">
          <Marquee
            speed={speed}
            pauseOnHover={pauseOnHover}
            pauseOnClick={true}
            gradient={false}
            className="overflow-y-hidden"
          >
            <div className="flex items-stretch py-3">
              {items.map((item, index) => (
                <div
                  key={item.id || index}
                  className={`shrink-0 mx-2.5 sm:mx-3 ${cardWidth}`}
                >
                  <HighlightCard item={item} />
                </div>
              ))}
            </div>
          </Marquee>
        </div>
      )}
    </div>
  );
};

/**
 * Individual Highlight Card matching the exact visual design:
 * - Golden amber border with rounded corners
 * - Top image clipped cleanly
 * - Bold deep red title
 * - Subtle and readable description text
 */
const HighlightCard = ({ item }) => {
  const { image, title, description, badge, alt } = item;

  return (
    <div className="group h-full flex flex-col bg-white rounded-2xl border-2 border-[#E5A93C] overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      {/* Top Image Container */}
      <div className="relative w-full h-44 sm:h-48 overflow-hidden bg-slate-100 shrink-0">
        <img
          src={image}
          alt={alt || title || "Festival highlight"}
          loading="lazy"
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
        {badge && (
          <span className="absolute top-2.5 right-2.5 bg-red/90 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full shadow-sm backdrop-blur-sm">
            {badge}
          </span>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-5 flex flex-col items-center text-center flex-grow justify-between">
        <div>
          {title && (
            <h3 className="text-red font-bold text-base sm:text-lg leading-snug tracking-tight mb-2">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-slate-700 text-xs sm:text-sm leading-relaxed font-normal">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FestivalHighlights;
